import { useEffect, useMemo, useRef, useState } from 'react';

import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';

import type {
  GetPortalLiveListingsQuery,
  ImportPortalListingsItem,
  UserSelectFragment,
} from '@graphql/__generated__/graphql';
import { UPDATE_PROPERTY } from '@modules/_shared/components/property-form/propertyFormQueries';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import { SYNC_PORTAL_LISTING } from '@modules/listings/lotsQueries';
import {
  GET_PORTAL,
  GET_PORTAL_LIVE_LISTINGS,
  GET_PORTAL_TEAMS,
  GET_PORTAL_TEAM_BROKERS,
  IMPORT_PORTAL_LISTINGS,
} from '@modules/portals/portalsQueries';
import { useLocale } from '@shared/hooks/locale';

const PAGE_SIZE = 10;

// Source of truth: the zod refinement in
// functions/src/hasura-actions/routes/toggle-portal-listing/idealista/schemas/property.ts
// (strict `areaUsable < areaConstructed`, so equal surfaces are rejected).
const AREA_USABLE_ERROR =
  'areaUsable must be always lower than areaConstructed';

type ImportSummary = {
  inserted: number;
  updated: number;
  errors: number;
  totalProperties: number;
  items: ImportPortalListingsItem[];
};

type ResyncRow = {
  externalPropertyId: string;
  lotId: string | null;
  success: boolean;
  autoFixed: boolean;
  warnings: string[];
  error: string | null;
};

type LiveListing = GetPortalLiveListingsQuery['portal_listings'][number];

// The action returns validation failures as GraphQL errors whose extensions
// hold the zod issues (same shape PortalPublicationErrorDialog reads).
const extensionIssues = (error: unknown): Array<{ message?: string }> => {
  if (!CombinedGraphQLErrors.is(error)) {
    return [];
  }
  return error.errors.flatMap(gqlError =>
    Array.isArray(gqlError.extensions) ? gqlError.extensions : [],
  );
};

const isAreaUsableError = (error: unknown): boolean =>
  extensionIssues(error).some(issue => issue?.message === AREA_USABLE_ERROR);

const resyncErrorText = (error: unknown): string => {
  const issues = extensionIssues(error)
    .map(issue => issue?.message)
    .filter((message): message is string => typeof message === 'string');
  if (issues.length > 0) {
    return issues.join('; ');
  }
  return error instanceof Error ? error.message : String(error);
};

// update_portal_listing.warnings is jsonb: entries are either plain strings
// or {message, raw, timestamp, type} objects (see useTogglePortalListing).
const warningMessages = (warnings: unknown): string[] => {
  if (!Array.isArray(warnings)) {
    return [];
  }
  return warnings
    .map(warning => {
      if (typeof warning === 'string') {
        return warning;
      }
      if (
        typeof warning === 'object' &&
        warning != null &&
        'message' in warning &&
        typeof warning.message === 'string'
      ) {
        return warning.message;
      }
      return null;
    })
    .filter((message): message is string => message != null);
};

// Mirrors extractSurfaceFromDummy in functions/src/utils/surface.ts: the
// value Idealista's areaUsable is filled from, selected by the lot's
// dummy_usable_surface. When the dummy IS built_surface the comparison is
// built vs built and no +1 can ever satisfy it — not auto-fixable.
const pickUsableSurface = (listing: LiveListing): number | null => {
  const lot = listing.lot;
  const property = lot.property;
  switch (lot.dummy_usable_surface) {
    case 'living_surface':
      return property.living_surface ?? null;
    case 'gross_surface':
      return property.gross_floor_surface ?? null;
    case 'weighted_surface':
      return property.weighted_floor_surface ?? null;
    case 'usable_surface':
      return property.usable_surface ?? null;
    default:
      // built_surface (unfixable) and unpublished (no comparison value).
      return null;
  }
};

const outcomeChipColor = (
  outcome: string,
): 'success' | 'info' | 'error' | 'default' => {
  switch (outcome) {
    case 'inserted':
    case 'would_insert':
      return 'success';
    case 'updated':
    case 'would_update':
    case 'attached':
    case 'would_attach':
      return 'info';
    case 'error':
      return 'error';
    default:
      return 'default';
  }
};

export const PortalImport: React.FC = () => {
  const { t } = useLocale();
  const { portalId } = useParams();

  const { data: portalData, refetch: refetchPortal } = useQuery(GET_PORTAL, {
    variables: { id: portalId ?? '' },
    skip: portalId == null,
  });
  const portalName = portalData?.portals_by_pk?.name ?? '';

  // The import/resync loops run across awaits; if the drawer tab unmounts
  // mid-run (each tab is its own <Route>) the loop must stop firing mutations
  // rather than keep racing a fresh run the user starts after re-opening.
  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const [brokerId, setBrokerId] = useState<string | null>(null);

  // Default the broker picker to brokers of the portal's linked team(s) — the
  // primary team plus any teams the portal is shared with.
  const { data: portalTeamsData } = useQuery(GET_PORTAL_TEAMS, {
    variables: { portal_id: portalId ?? '' },
    skip: portalId == null,
  });
  const teamIds = useMemo<string[]>(() => {
    const portal = portalTeamsData?.portals_by_pk;
    if (portal == null) {
      return [];
    }
    const ids = [
      portal.team_id,
      ...portal.teams_portals.map(tp => tp.team_id),
    ].filter((id): id is string => id != null);
    return [...new Set(ids)];
  }, [portalTeamsData?.portals_by_pk]);

  const { data: teamBrokersData } = useQuery(GET_PORTAL_TEAM_BROKERS, {
    variables: { team_ids: teamIds },
    skip: teamIds.length === 0,
  });
  const teamBrokerOptions = useMemo<UserSelectFragment[]>(() => {
    const unique = new Map<string, UserSelectFragment>();
    for (const entry of teamBrokersData?.teams_users ?? []) {
      if (entry.user != null) {
        unique.set(entry.user.id, entry.user);
      }
    }
    return Array.from(unique.values());
  }, [teamBrokersData?.teams_users]);

  const [importListings] = useMutation(IMPORT_PORTAL_LISTINGS);
  const [syncListing] = useMutation(SYNC_PORTAL_LISTING);
  const [updateProperty] = useMutation(UPDATE_PROPERTY);
  const { refetch: fetchLiveListings } = useQuery(GET_PORTAL_LIVE_LISTINGS, {
    variables: { portal_id: portalId ?? '' },
    skip: true,
  });

  const [running, setRunning] = useState(false);
  // Which import action is in flight, so only the clicked button spins.
  const [importMode, setImportMode] = useState<'preview' | 'run' | null>(null);
  const [progress, setProgress] = useState<{
    page: number;
    totalPages: number;
  } | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Set when a run fails part-way so the CSM can resume from the failed page
  // (imports dedupe on external_property_id, so replaying pages is safe). The
  // broker is captured too: the picker re-enables on failure, so resuming must
  // replay the later pages with the same assignee the earlier pages used.
  const [resume, setResume] = useState<{
    dryRun: boolean;
    page: number;
    brokerId: string | null;
  } | null>(null);

  const [resyncRunning, setResyncRunning] = useState(false);
  const [resyncProgress, setResyncProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [resyncRows, setResyncRows] = useState<ResyncRow[] | null>(null);
  const [resyncStopped, setResyncStopped] = useState(false);
  const resyncStopRequested = useRef(false);

  const runImport = async (
    dryRun: boolean,
    startPage = 1,
    // Captured once at run start so a resume replays later pages with the same
    // broker, even if the picker was changed after the failure that paused it.
    runBrokerId: string | null = brokerId,
  ) => {
    if (portalId == null) {
      return;
    }
    setRunning(true);
    setImportMode(dryRun ? 'preview' : 'run');
    setError(null);
    setResume(null);
    if (startPage === 1) {
      setSummary(null);
    }

    let page = startPage;
    let totalPages = startPage;
    try {
      do {
        // Stop if the tab unmounted mid-run — otherwise this keeps inserting
        // while the UI shows idle and a second run can race the dedupe.
        if (cancelledRef.current) {
          return;
        }
        const { data } = await importListings({
          variables: {
            portal_id: portalId,
            broker_id: runBrokerId,
            dry_run: dryRun,
            page,
            page_size: PAGE_SIZE,
          },
        });
        if (cancelledRef.current) {
          return;
        }
        const out = data?.import_portal_listings;
        if (out == null) {
          throw new Error(t('The import returned no result'));
        }
        totalPages = out.total_pages;
        setProgress({ page, totalPages });
        // Functional update: the previous summary is the single source of the
        // running totals (a fresh run reset it to null above; a resume keeps
        // accumulating into the totals of the interrupted run).
        setSummary(prev => ({
          inserted: (prev?.inserted ?? 0) + out.inserted,
          updated: (prev?.updated ?? 0) + out.updated,
          errors: (prev?.errors ?? 0) + out.errors,
          // When the feed omits a real total the backend falls back to a
          // page-local count — keep the largest value seen so a multi-page
          // run never ends up reporting the last page's count as the total.
          totalProperties: Math.max(
            prev?.totalProperties ?? 0,
            out.total_properties,
          ),
          items: [...(prev?.items ?? []), ...out.items],
        }));
        page += 1;
      } while (page <= totalPages);
      if (!dryRun) {
        // A real import created lots/portal_listings; the portals list's
        // live-listings count and cached lot lists would otherwise stay stale
        // until a hard reload. Refresh the portal to pick up the new state.
        void refetchPortal();
      }
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : String(caught));
      setResume({ dryRun, page, brokerId: runBrokerId });
    } finally {
      setRunning(false);
      setImportMode(null);
      setProgress(null);
    }
  };

  const handleRunImport = () => {
    if (
      window.confirm(
        t(
          'Import all properties from {{portal}} into the CRM? New properties are created as lots (with their images); properties already in the CRM are updated or linked to the matching lot.',
          { portal: portalName },
        ),
      )
    ) {
      void runImport(false);
    }
  };

  // Resync one listing; on the specific "usable >= built" Idealista
  // validation error, bump built_surface to usable + 1 and retry once.
  const resyncListing = async (listing: LiveListing): Promise<ResyncRow> => {
    const base = {
      externalPropertyId: listing.external_property_id ?? listing.id,
      lotId: listing.lot_id,
      autoFixed: false,
    };
    try {
      const { data: syncData } = await syncListing({
        variables: { portalId: portalId ?? '', lotId: listing.lot_id },
      });
      return {
        ...base,
        success: syncData?.update_portal_listing?.success ?? false,
        warnings: warningMessages(syncData?.update_portal_listing?.warnings),
        error: null,
      };
    } catch (caught: unknown) {
      const usable = isAreaUsableError(caught)
        ? pickUsableSurface(listing)
        : null;
      if (usable == null) {
        return {
          ...base,
          success: false,
          warnings: [],
          error: resyncErrorText(caught),
        };
      }
      // The tab may have unmounted while the first sync request was in flight;
      // don't fire the auto-fix mutations into an unmounted view. The loop
      // discards this row once it re-checks the cancel flag.
      if (cancelledRef.current) {
        return { ...base, success: false, warnings: [], error: null };
      }
      // Only claim the built-surface auto-fix once the property update itself
      // landed — if updateProperty throws, nothing changed, so the row must
      // not show "built surface fixed" nor inflate the auto-fixed tally.
      let builtSurfaceFixed = false;
      try {
        await updateProperty({
          variables: {
            id: listing.lot.property.id,
            property: { built_surface: Math.round(usable) + 1 },
          },
        });
        builtSurfaceFixed = true;
        // The tab may have unmounted while updateProperty was in flight. The
        // built_surface change already landed (a permanent edit), but don't
        // fire the retry publish into an unmounted view — the loop discards
        // this row on its next cancel check.
        if (cancelledRef.current) {
          return {
            ...base,
            autoFixed: true,
            success: false,
            warnings: [],
            error: null,
          };
        }
        const { data: retryData } = await syncListing({
          variables: { portalId: portalId ?? '', lotId: listing.lot_id },
        });
        return {
          ...base,
          autoFixed: true,
          success: retryData?.update_portal_listing?.success ?? false,
          warnings: warningMessages(retryData?.update_portal_listing?.warnings),
          error: null,
        };
      } catch (retryCaught: unknown) {
        return {
          ...base,
          autoFixed: builtSurfaceFixed,
          success: false,
          warnings: [],
          error: resyncErrorText(retryCaught),
        };
      }
    }
  };

  const runResync = async () => {
    if (portalId == null) {
      return;
    }
    setResyncRunning(true);
    setResyncRows(null);
    setResyncStopped(false);
    resyncStopRequested.current = false;
    // Clear any leftover import error/resume: otherwise a stale "Resume" button
    // from a failed import survives into the resync flow and would restart the
    // import at the old page.
    setError(null);
    setResume(null);
    try {
      const { data } = await fetchLiveListings({ portal_id: portalId });
      const listings = data?.portal_listings ?? [];
      setResyncRows([]);
      setResyncProgress({ done: 0, total: listings.length });
      for (let index = 0; index < listings.length; index++) {
        // Stop firing mutations once the tab unmounts.
        if (cancelledRef.current) {
          return;
        }
        if (resyncStopRequested.current) {
          setResyncStopped(true);
          break;
        }
        const row = await resyncListing(listings[index]);
        if (cancelledRef.current) {
          return;
        }
        setResyncRows(prev => [...(prev ?? []), row]);
        setResyncProgress({ done: index + 1, total: listings.length });
      }
      // Resync flips portal_listings.live; refresh the portal so live counts
      // and cached lot lists reflect it without a hard reload.
      void refetchPortal();
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setResyncRunning(false);
    }
  };

  const busy = running || resyncRunning;
  const listingsSearch = new URLSearchParams({
    where: JSON.stringify({
      portal_listings: {
        portal_id: { _eq: portalId },
        live: { _eq: true },
      },
    }),
  }).toString();

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          {t('Assign imported listings to a broker')}
        </Typography>
        <UserSelect
          userId={brokerId ?? undefined}
          onChange={id => setBrokerId(typeof id === 'string' ? id : null)}
          filters={{ is_broker: { _eq: true } }}
          defaultOptions={teamBrokerOptions}
          // Show the portal team's brokers (defaultOptions) when the field is
          // clicked — the picker otherwise only reveals its list once the user
          // types, so the loaded team brokers would never surface on their own.
          openOnFocus
          disabled={busy}
        />
      </Box>

      <Stack direction="row" spacing={2}>
        <Button
          variant="outlined"
          disabled={busy}
          loading={importMode === 'preview'}
          onClick={() => void runImport(true)}
        >
          {t('Preview (dry run)')}
        </Button>
        <Button
          variant="contained"
          disabled={busy}
          loading={importMode === 'run'}
          onClick={handleRunImport}
        >
          {t('Run import')}
        </Button>
      </Stack>

      {progress != null && (
        <Box>
          <Typography variant="body2" gutterBottom>
            {t('Processing page {{page}} of {{total}}…', {
              page: progress.page,
              total: progress.totalPages,
            })}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={
              progress.totalPages > 0
                ? (progress.page / progress.totalPages) * 100
                : 0
            }
          />
        </Box>
      )}

      {error != null && (
        <Alert
          severity="error"
          action={
            resume != null ? (
              <Button
                color="inherit"
                size="small"
                disabled={busy}
                onClick={() =>
                  void runImport(resume.dryRun, resume.page, resume.brokerId)
                }
              >
                {t('Resume')}
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      )}

      {summary != null && (
        <Box>
          <Alert severity={summary.errors > 0 ? 'warning' : 'success'}>
            {t(
              '{{total}} found: {{inserted}} new, {{updated}} already imported, {{errors}} with errors',
              {
                total: summary.totalProperties,
                inserted: summary.inserted,
                updated: summary.updated,
                errors: summary.errors,
              },
            )}
          </Alert>
          <Table size="small" sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('Property')}</TableCell>
                <TableCell>{t('Outcome')}</TableCell>
                <TableCell>{t('Notes')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.items.map(item => (
                <TableRow key={item.external_property_id}>
                  <TableCell>
                    <Typography variant="body2">
                      {item.label ?? item.external_property_id}
                    </Typography>
                    {item.reference != null && (
                      <Typography variant="caption" color="text.secondary">
                        {item.reference}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.outcome}
                      color={outcomeChipColor(item.outcome)}
                    />
                  </TableCell>
                  <TableCell>
                    {(item.errors ?? []).map(err => (
                      <Typography
                        key={err}
                        variant="caption"
                        color="error"
                        sx={{ display: 'block' }}
                      >
                        {err}
                      </Typography>
                    ))}
                    {(item.warnings ?? []).map(warning => (
                      <Typography
                        key={warning}
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {warning}
                      </Typography>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          {t('Resync online listings')}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t(
            'Re-publishes every live listing of this portal so the CRM reference replaces the portal reference. Run after importing. To satisfy Idealista, a listing whose built surface is not greater than its usable surface has its built surface permanently set to usable + 1.',
          )}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            disabled={busy}
            loading={resyncRunning}
            onClick={() => void runResync()}
          >
            {t('Resync all online listings')}
          </Button>
          {resyncRunning && (
            <Button
              color="warning"
              onClick={() => {
                resyncStopRequested.current = true;
              }}
            >
              {t('Stop')}
            </Button>
          )}
        </Stack>
        {resyncProgress != null && resyncRunning && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              {t('Resyncing {{done}} of {{total}}…', {
                done: resyncProgress.done,
                total: resyncProgress.total,
              })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                resyncProgress.total > 0
                  ? (resyncProgress.done / resyncProgress.total) * 100
                  : 0
              }
            />
          </Box>
        )}
        {resyncRows != null && (
          <Box sx={{ mt: 2 }}>
            <Alert
              severity={
                resyncRows.some(row => !row.success) ? 'warning' : 'success'
              }
            >
              {t(
                '{{ok}} of {{total}} listings resynced ({{fixed}} auto-fixed, {{failed}} failed)',
                {
                  ok: resyncRows.filter(row => row.success).length,
                  // The full listing count, not just the attempted rows, so a
                  // stopped run does not read "20 of 20" above "stopped at 20
                  // of 100".
                  total: resyncProgress?.total ?? resyncRows.length,
                  fixed: resyncRows.filter(row => row.autoFixed).length,
                  failed: resyncRows.filter(row => !row.success).length,
                },
              )}
              {resyncStopped &&
                ` — ${t('stopped at {{done}} of {{total}}', {
                  done: resyncRows.length,
                  total: resyncProgress?.total ?? resyncRows.length,
                })}`}
            </Alert>
            {resyncRows.some(
              row => !row.success || row.autoFixed || row.warnings.length > 0,
            ) && (
              <Table size="small" sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('Listing')}</TableCell>
                    <TableCell>{t('Result')}</TableCell>
                    <TableCell>{t('Details')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resyncRows
                    .filter(
                      row =>
                        !row.success ||
                        row.autoFixed ||
                        row.warnings.length > 0,
                    )
                    .map(row => (
                      <TableRow key={row.externalPropertyId}>
                        <TableCell>
                          {row.lotId != null ? (
                            <Link
                              component={RouterLink}
                              to={`/listings/${row.lotId}/marketing`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {row.externalPropertyId}
                            </Link>
                          ) : (
                            row.externalPropertyId
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Chip
                              size="small"
                              label={row.success ? t('resynced') : t('failed')}
                              color={row.success ? 'success' : 'error'}
                            />
                            {row.autoFixed && (
                              <Chip
                                size="small"
                                label={t('built surface fixed (+1)')}
                                color="info"
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {row.error != null && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ display: 'block' }}
                            >
                              {row.error}
                            </Typography>
                          )}
                          {row.warnings.map(warning => (
                            <Typography
                              key={warning}
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {warning}
                            </Typography>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </Box>

      <Alert severity="info">
        <AlertTitle>{t('Next steps')}</AlertTitle>
        {t(
          'Fix any listed errors, verify the CRM references replaced the portal references, and make a test enquiry.',
        )}{' '}
        <Link href={`/listings?${listingsSearch}`}>{t('View listings')}</Link>
      </Alert>
    </Box>
  );
};
